import {
  RecordTableCellView,
  RecordTableGridView,
  RecordTableHeaderCellView,
  RecordTableHeaderRowView,
  RecordTableRowView,
} from "@product/app/workspace/channels/[channelId]/record-table-grid-view";
import { RecordOptionLabel } from "@product/app/workspace/channels/[channelId]/record-option-badge";
import {
  ACTIONS_COLUMN_SIZE,
  ROW_HEIGHT,
  fieldColumnSize,
} from "@product/app/workspace/channels/[channelId]/record-table-state";
import { EditableCellView, RecordRowActionsView } from "@product/app/workspace/channels/[channelId]/record-table-view";
import { TableColumnResizeHandle } from "@product/components/ui/table-column-resize-handle";

import type { ShotChannelTable, ShotTableCell } from "./channel-table";

/** A cell's value as RecordFieldValue prints it: an option's badge, or the formatted text. */
export function ShotCellValue({ cell }: { cell: ShotTableCell }) {
  return cell.option ? <RecordOptionLabel option={cell.option} /> : <>{cell.display}</>;
}

/**
 * The table's grid at rest, as RecordTableGrid renders it through its views:
 * the pinned actions column, then a column per field at its default width,
 * each header a sort button (the sorted one with its arrow) with its resize
 * handle, and every row where the virtualizer places it, `index *
 * ROW_HEIGHT` down the body. No cell is active; each holds the product's
 * resting view.
 */
export function ShotChannelGrid({ table }: { table: ShotChannelTable }) {
  const widths = table.fields.map((field) => fieldColumnSize(field).size);
  const tableWidth = ACTIONS_COLUMN_SIZE.size + widths.reduce((sum, width) => sum + width, 0);
  return (
    <RecordTableGridView
      tableWidth={tableWidth}
      bodyHeight={table.rows.length * ROW_HEIGHT}
      header={
        <RecordTableHeaderRowView>
          <RecordTableHeaderCellView width={ACTIONS_COLUMN_SIZE.size} pinned label="" />
          {table.fields.map((field, column) => (
            <RecordTableHeaderCellView
              key={field.key}
              width={widths[column]!}
              label={field.label}
              name={field.label}
              sortable
              sorted={field.key === table.sortField.key ? "asc" : null}
              resizeHandle={<TableColumnResizeHandle aria-label={`Resize ${field.label}`} active={false} />}
            />
          ))}
        </RecordTableHeaderRowView>
      }
    >
      {table.rows.map((row, index) => (
        <RecordTableRowView key={row.id} index={index} start={index * ROW_HEIGHT} tableWidth={tableWidth}>
          <RecordTableCellView width={ACTIONS_COLUMN_SIZE.size} pinned>
            <RecordRowActionsView record={row} />
          </RecordTableCellView>
          {table.fields.map((field, column) => {
            const cell = row.cells[field.key]!;
            return (
              <RecordTableCellView key={field.key} width={widths[column]!} role="gridcell" tabIndex={-1}>
                <EditableCellView title={cell.title}>
                  <ShotCellValue cell={cell} />
                </EditableCellView>
              </RecordTableCellView>
            );
          })}
        </RecordTableRowView>
      ))}
    </RecordTableGridView>
  );
}
